import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/axios";

/** Max wait for the OS location prompt; call proceeds after deny/timeout. */
const GPS_USER_TIMEOUT_MS = 10000;

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.turnstile) return Promise.resolve(true);
  if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
    return new Promise((resolve) => {
      const tick = () => (window.turnstile ? resolve(true) : setTimeout(tick, 50));
      tick();
    });
  }
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => resolve(Boolean(window.turnstile));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

/** Opens dialer in the same user-activation turn as the click (avoids Chrome "Continue to Phone?" when possible). */
export function openDialerFromUserGesture(telUri) {
  if (!telUri || typeof window === "undefined") return;
  const anchor = document.createElement("a");
  anchor.href = telUri;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Waits for the bystander to respond to the OS location prompt (allow, deny, or timeout). */
function requestGpsFromUser() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ coords: null, status: "unsupported" });
      return;
    }
    let settled = false;
    const finish = (coords, status) => {
      if (settled) return;
      settled = true;
      resolve({ coords, status });
    };
    const timer = window.setTimeout(() => finish(null, "timeout"), GPS_USER_TIMEOUT_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        finish(
          {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            geoAccuracyMeters: pos.coords.accuracy,
          },
          "granted",
        );
      },
      () => {
        window.clearTimeout(timer);
        finish(null, "denied");
      },
      {
        enableHighAccuracy: false,
        timeout: GPS_USER_TIMEOUT_MS,
        maximumAge: 120000,
      },
    );
  });
}

async function getTurnstileToken(siteKey, containerRef) {
  if (!siteKey) return undefined;
  const ok = await loadTurnstileScript();
  if (!ok || !window.turnstile || !containerRef?.current) return undefined;
  return new Promise((resolve) => {
    try {
      window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        size: "invisible",
        callback: (token) => resolve(token),
        "error-callback": () => resolve(undefined),
        "timeout-callback": () => resolve(undefined),
      });
    } catch {
      resolve(undefined);
    }
  });
}

/**
 * Step-by-step call reveal: intro → location (OS prompt) → API → dial (user tap opens dialer).
 */
export function useCallReveal({ qrId, turnstileSiteKey }) {
  const [modalOpen, setModalOpen] = useState(false);
  /** intro | location | calling | dial | error */
  const [modalPhase, setModalPhase] = useState("intro");
  const [pendingTarget, setPendingTarget] = useState(null);
  const [locationStatus, setLocationStatus] = useState(null);
  const [error, setError] = useState(null);
  const [readyTelUri, setReadyTelUri] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWarmRef = useRef(null);
  const gpsResultRef = useRef(null);

  const resetModal = useCallback(() => {
    setModalOpen(false);
    setModalPhase("intro");
    setPendingTarget(null);
    setLocationStatus(null);
    setError(null);
    setReadyTelUri(null);
    gpsResultRef.current = null;
  }, []);

  useEffect(() => {
    if (!modalOpen || !turnstileSiteKey) return;
    loadTurnstileScript();
  }, [modalOpen, turnstileSiteKey]);

  const warmTurnstile = useCallback(() => {
    if (!turnstileSiteKey || turnstileWarmRef.current) return;
    turnstileWarmRef.current = getTurnstileToken(turnstileSiteKey, turnstileRef);
  }, [turnstileSiteKey]);

  const executeReveal = useCallback(
    async (target, gps, turnstileTokenPromise) => {
      if (!target?.id || !qrId) return;
      setModalPhase("calling");
      setError(null);
      try {
        const turnstileToken =
          turnstileTokenPromise != null ? await turnstileTokenPromise : await getTurnstileToken(turnstileSiteKey, turnstileRef);
        const res = await api.post(`/emergency/${qrId}/reveal-call`, {
          targetId: target.id,
          ...(turnstileToken ? { turnstileToken } : {}),
          ...(gps || {}),
        });
        const telUri = res.data?.telUri;
        if (!telUri) throw new Error("missing tel");
        setReadyTelUri(telUri);
        setModalPhase("dial");
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          (err.response?.status === 429 ? "Too many attempts. Please wait and try again." : null) ||
          "Could not start the call. Try again or dial 112.";
        setError(msg);
        setModalPhase("error");
      }
    },
    [qrId, turnstileSiteKey],
  );

  const beginCallReveal = useCallback(
    (target) => {
      if (!target?.id) return;
      setPendingTarget(target);
      setModalOpen(true);
      setModalPhase("intro");
      setLocationStatus(null);
      setError(null);
      setReadyTelUri(null);
      gpsResultRef.current = null;
      turnstileWarmRef.current = null;
      loadTurnstileScript();
    },
    [],
  );

  const continueToLocationStep = useCallback(() => {
    setModalPhase("location");
    warmTurnstile();
  }, [warmTurnstile]);

  const requestLocationThenCall = useCallback(async () => {
    setLocationStatus("waiting");
    warmTurnstile();
    const turnstilePromise = turnstileWarmRef.current || getTurnstileToken(turnstileSiteKey, turnstileRef);
    turnstileWarmRef.current = turnstilePromise;
    const [gpsResult] = await Promise.all([requestGpsFromUser(), turnstilePromise]);
    const { coords, status } = gpsResult;
    gpsResultRef.current = coords;
    setLocationStatus(status);
    await executeReveal(pendingTarget, coords, turnstilePromise);
  }, [pendingTarget, executeReveal, turnstileSiteKey, warmTurnstile]);

  const skipLocationAndCall = useCallback(async () => {
    setLocationStatus("skipped");
    warmTurnstile();
    const turnstilePromise = turnstileWarmRef.current || getTurnstileToken(turnstileSiteKey, turnstileRef);
    await executeReveal(pendingTarget, null, turnstilePromise);
  }, [pendingTarget, executeReveal, turnstileSiteKey, warmTurnstile]);

  const placeCallNow = useCallback(() => {
    if (!readyTelUri) return;
    openDialerFromUserGesture(readyTelUri);
    resetModal();
  }, [readyTelUri, resetModal]);

  return {
    beginCallReveal,
    closeModal: resetModal,
    modalOpen,
    modalPhase,
    pendingTarget,
    locationStatus,
    error,
    turnstileRef,
    continueToLocationStep,
    requestLocationThenCall,
    skipLocationAndCall,
    placeCallNow,
  };
}
